import { useQuery } from '@tanstack/react-query';
import { fetchWaivers } from '../../services/waivers';
import useUser from '../global/useUser';
import useFeatureGuard from '../subscriptions/useFeatureGuard';
import { Feature } from '../../types/subscriptions';

function useGetWaivers({
  serviceId,
  waiverName,
  enabled: isEnabled = true,
}: {
  serviceId?: string | number;
  waiverName?: string;
  enabled?: boolean;
} = {}) {
  const { user } = useUser();

  const canManageWaivers = useFeatureGuard(Feature.WAIVERS);

  const enabled =
    isEnabled && !!user && !!user?.business?.id && canManageWaivers;
  const waiverServiceId =
    serviceId && ![0, '0'].includes(serviceId.toString())
      ? parseInt(String(serviceId), 10)
      : undefined;

  const query = useQuery({
    // eslint-disable-next-line @tanstack/query/exhaustive-deps
    queryKey: [
      'waivers',
      user?.userId,
      user?.business?.id,
      waiverServiceId,
      waiverName,
    ],
    queryFn: async () => {
      const data = await fetchWaivers({
        businessId: user?.business?.id!,
        serviceId: waiverServiceId || undefined,
        waiverName,
      });
      return data;
    },
    enabled,
  });

  const isPublishedEnabled = !!query.data?.published?.enabled;
  const showWaiversFeatures = canManageWaivers && isPublishedEnabled;

  return {
    data: query.data,
    isPlaceholderData: query.isPlaceholderData,
    isPublishedEnabled,
    isLoading: query.isLoading,
    isError: query.isError,
    isFetching: query.isFetching,
    isRefetching: query.isRefetching,
    refetch: query.refetch,
    showWaiversFeatures,
  };
}

export default useGetWaivers;
